
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Article } from "@/types/article";

export const useArticles = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchArticles = async () => {
    try {
      const memberId = sessionStorage.getItem('organization_member_id');
      if (!memberId) {
        navigate('/manager');
        return;
      }

      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('id', memberId)
        .single();

      if (memberError) throw memberError;

      const { data, error } = await supabase
        .from('organization_news')
        .select('*')
        .eq('organization_id', memberData.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const createArticle = async (title: string, content: string, imageUrl?: string) => {
    try {
      const memberId = sessionStorage.getItem('organization_member_id');
      if (!memberId) {
        navigate('/manager');
        return false;
      }

      const { data: memberData } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('id', memberId)
        .single();

      const { error } = await supabase
        .from('organization_news')
        .insert({
          title,
          content,
          created_by: memberId,
          organization_id: memberData.organization_id,
          image_url: imageUrl
        });

      if (error) throw error;

      toast.success('Article created successfully');
      await fetchArticles();
      return true;
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error('Failed to create article');
      return false;
    }
  };

  const updateArticle = async (id: string, title: string, content: string) => {
    try {
      const { error } = await supabase
        .from('organization_news')
        .update({ title, content })
        .eq('id', id);

      if (error) throw error;

      toast.success('Article updated successfully');
      await fetchArticles();
      return true;
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error('Failed to update article');
      return false;
    }
  };

  const deleteArticle = async (id: string) => {
    try {
      const { error } = await supabase
        .from('organization_news')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Article deleted successfully');
      await fetchArticles();
      return true;
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
      return false;
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  return {
    articles,
    loading,
    createArticle,
    updateArticle,
    deleteArticle
  };
};
